/**
 * PreviewCanvas - PixiJS-based canvas for cutscene preview and playback
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { Application, Container, Sprite, Assets, Graphics } from "pixi.js";
import {
  gridMarginX,
  gridMarginY,
  gridTileSize,
  GRID_WIDTH,
  GRID_HEIGHT,
} from "types/battle-engine/constants";
import {
  CutsceneData,
  CutsceneActor,
  Position,
  CutsceneEvent,
} from "@/lib/cutscene/types";
import {
  CutsceneEngine,
  CutsceneEngineCallbacks,
  animatePosition,
  animateValue,
  shakeScreen,
  typewriterEffect,
} from "@/lib/cutscene/CutsceneEngine";

interface PreviewCanvasProps {
  cutscene: CutsceneData;
  isPlaying: boolean;
  onPlayStateChange: (playing: boolean) => void;
  onEventIndexChange: (index: number) => void;
  currentEventIndex: number;
  /** Callback when actor positions change (for editor sync) */
  onActorsChange?: (actors: Record<string, CutsceneActor>) => void;
  /** Show grid overlay */
  showGrid?: boolean;
  /** Dialog state for external dialog component */
  dialogState: {
    visible: boolean;
    speaker: string;
    portrait?: string;
    text: string;
  };
  onDialogStateChange: (state: {
    visible: boolean;
    speaker: string;
    portrait?: string;
    text: string;
  }) => void;
  /** Fade overlay state */
  fadeState: {
    visible: boolean;
    opacity: number;
    color: string;
  };
  onFadeStateChange: (state: {
    visible: boolean;
    opacity: number;
    color: string;
  }) => void;
}

export const PreviewCanvas: React.FC<PreviewCanvasProps> = ({
  cutscene,
  isPlaying,
  onPlayStateChange,
  onEventIndexChange,
  currentEventIndex,
  onActorsChange,
  showGrid = false,
  dialogState,
  onDialogStateChange,
  fadeState,
  onFadeStateChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const worldRef = useRef<Container | null>(null);
  const engineRef = useRef<CutsceneEngine | null>(null);
  const actorsRef = useRef<Record<string, CutsceneActor>>({});
  const actorSpritesRef = useRef<Record<string, Container>>({});
  const vfxSpritesRef = useRef<Record<string, Container>>({});
  const mapSpriteRef = useRef<Sprite | null>(null);
  const gridGraphicsRef = useRef<Graphics | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize PixiJS
  useEffect(() => {
    let isCancelled = false;
    let appToDestroy: Application | null = null;

    const initApp = async () => {
      if (!containerRef.current || isCancelled) return;

      // Check if already initialized
      const existingCanvas = containerRef.current.querySelector("canvas");
      if (existingCanvas) return;

      const app = new Application();

      try {
        await app.init({
          background: "#161e29",
          resizeTo: containerRef.current,
          antialias: false,
        });
      } catch (error) {
        // Init failed (possibly due to unmount during async init)
        return;
      }

      // Check cancellation after async operation
      if (isCancelled || !containerRef.current) {
        app.destroy(true);
        return;
      }

      // Store reference for cleanup
      appToDestroy = app;

      containerRef.current.appendChild(app.canvas);
      app.stage.sortableChildren = true;

      // Create world container for camera control
      const worldContainer = new Container();
      worldContainer.sortableChildren = true;
      app.stage.addChild(worldContainer);

      // Calculate camera settings to center grid
      const gridWidth = GRID_WIDTH * gridTileSize;
      const gridHeight = GRID_HEIGHT * gridTileSize;
      const gridCenterX = gridMarginX + gridWidth / 2;
      const gridCenterY = gridMarginY + gridHeight / 2;

      const scaleX = containerRef.current.clientWidth / gridWidth;
      const scaleY = containerRef.current.clientHeight / gridHeight;
      const scale = Math.min(scaleX, scaleY, 1) * 0.9;

      worldContainer.scale.set(scale);
      worldContainer.position.set(
        containerRef.current.clientWidth / 2 - gridCenterX * scale,
        containerRef.current.clientHeight / 2 - gridCenterY * scale,
      );

      // Create grid graphics
      const gridGraphics = new Graphics();
      gridGraphics.zIndex = -500;
      worldContainer.addChild(gridGraphics);
      gridGraphicsRef.current = gridGraphics;

      appRef.current = app;
      worldRef.current = worldContainer;
      setIsInitialized(true);
    };

    initApp();

    return () => {
      isCancelled = true;
      // Only destroy if app was fully initialized
      if (appToDestroy) {
        appToDestroy.destroy(true);
      }
      appRef.current = null;
      worldRef.current = null;
      gridGraphicsRef.current = null;
    };
  }, []);

  // Load map when cutscene changes
  useEffect(() => {
    if (!isInitialized || !worldRef.current) return;

    const loadMap = async () => {
      const world = worldRef.current!;

      // Remove old map
      if (mapSpriteRef.current) {
        world.removeChild(mapSpriteRef.current);
        mapSpriteRef.current.destroy();
        mapSpriteRef.current = null;
      }

      try {
        const mapImage = await Assets.load(
          `/assets/raids/maps/${cutscene.mapId}.webp`,
        );
        const mapSprite = new Sprite(mapImage);

        const gridWidth = GRID_WIDTH * gridTileSize;
        const gridHeight = GRID_HEIGHT * gridTileSize;
        const mapGridCenterX = gridMarginX + gridWidth / 2;
        const mapGridCenterY = gridMarginY + gridHeight / 2;

        mapSprite.x = mapGridCenterX;
        mapSprite.y = mapGridCenterY;
        mapSprite.scale.set(0.5);
        mapSprite.anchor.set(0.5, 0.5);
        mapSprite.zIndex = -1000;

        world.addChild(mapSprite);
        mapSpriteRef.current = mapSprite;
      } catch (error) {
        console.error("[PreviewCanvas] Failed to load map:", error);
      }
    };

    loadMap();
  }, [isInitialized, cutscene.mapId]);

  // Draw grid overlay
  useEffect(() => {
    if (!gridGraphicsRef.current) return;

    const g = gridGraphicsRef.current;
    g.clear();

    if (!showGrid) return;

    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        const posX = gridMarginX + x * gridTileSize;
        const posY = gridMarginY + y * gridTileSize;

        g.rect(posX, posY, gridTileSize, gridTileSize);
        g.stroke({ width: 1, color: 0xffffff, alpha: 0.2 });
      }
    }
  }, [showGrid, isInitialized]);

  // Apply initial camera settings
  useEffect(() => {
    if (!worldRef.current || !containerRef.current) return;

    const world = worldRef.current;
    const container = containerRef.current;

    const gridWidth = GRID_WIDTH * gridTileSize;
    const gridHeight = GRID_HEIGHT * gridTileSize;

    const baseScaleX = container.clientWidth / gridWidth;
    const baseScaleY = container.clientHeight / gridHeight;
    const baseScale = Math.min(baseScaleX, baseScaleY, 1) * 0.9;

    const finalScale = baseScale * cutscene.camera.initialZoom;
    world.scale.set(finalScale);

    const targetX = cutscene.camera.initialPosition.x;
    const targetY = cutscene.camera.initialPosition.y;

    world.position.set(
      container.clientWidth / 2 - targetX * finalScale,
      container.clientHeight / 2 - targetY * finalScale,
    );
  }, [isInitialized, cutscene.camera]);

  // Create engine callbacks
  const createCallbacks = useCallback((): CutsceneEngineCallbacks => {
    const world = worldRef.current!;
    const container = containerRef.current!;

    return {
      onSpawnPokemon: async (event) => {
        console.log("[PreviewCanvas] onSpawnPokemon:", event);
        // Create actor entry
        const actor: CutsceneActor = {
          id: event.actorId,
          pokemonName: event.pokemonName,
          position: { ...event.position },
          direction: event.direction,
          animation: event.animation,
          scale: event.scale || 1,
          visible: true,
        };
        actorsRef.current[event.actorId] = actor;
        onActorsChange?.({ ...actorsRef.current });
        console.log(
          "[PreviewCanvas] Actor created, actors:",
          actorsRef.current,
        );

        // Create sprite container (placeholder - actual sprite rendering handled by React overlay)
        const spriteContainer = new Container();
        spriteContainer.x = event.position.x;
        spriteContainer.y = event.position.y;
        spriteContainer.zIndex = Math.floor(event.position.y);
        world.addChild(spriteContainer);
        actorSpritesRef.current[event.actorId] = spriteContainer;
      },

      onMovePokemon: async (event, easing) => {
        console.log(
          "[PreviewCanvas] onMovePokemon:",
          event,
          "actors:",
          actorsRef.current,
        );
        const actor = actorsRef.current[event.actorId];
        const spriteContainer = actorSpritesRef.current[event.actorId];
        if (!actor || !spriteContainer) {
          console.warn(
            "[PreviewCanvas] Actor not found for move:",
            event.actorId,
          );
          return;
        }

        const startPos = { ...actor.position };

        await animatePosition(
          startPos,
          event.position,
          event.duration,
          easing,
          (pos) => {
            actor.position = pos;
            spriteContainer.x = pos.x;
            spriteContainer.y = pos.y;
            spriteContainer.zIndex = Math.floor(pos.y);
            onActorsChange?.({ ...actorsRef.current });
          },
          abortControllerRef.current?.signal,
        );
      },

      onAnimatePokemon: async (event) => {
        const actor = actorsRef.current[event.actorId];
        if (!actor) return;

        actor.animation = event.animation;
        if (event.direction) {
          actor.direction = event.direction;
        }
        onActorsChange?.({ ...actorsRef.current });

        if (event.duration && event.duration > 0) {
          await new Promise((resolve) => setTimeout(resolve, event.duration));
        }
      },

      onRemovePokemon: async (event) => {
        const spriteContainer = actorSpritesRef.current[event.actorId];
        if (spriteContainer) {
          if (event.fadeOutDuration && event.fadeOutDuration > 0) {
            await animateValue(
              1,
              0,
              event.fadeOutDuration,
              (t) => t,
              (alpha) => {
                spriteContainer.alpha = alpha;
              },
              abortControllerRef.current?.signal,
            );
          }
          world.removeChild(spriteContainer);
          spriteContainer.destroy();
          delete actorSpritesRef.current[event.actorId];
        }

        delete actorsRef.current[event.actorId];
        onActorsChange?.({ ...actorsRef.current });
      },

      onPlayVFX: async (event) => {
        // VFX will be rendered via React overlay using AnimatedVFX component
        // Store position for overlay to read
        const vfxContainer = new Container();
        vfxContainer.x = event.position.x;
        vfxContainer.y = event.position.y;
        vfxContainer.zIndex = 9000;
        world.addChild(vfxContainer);
        vfxSpritesRef.current[event.vfxId] = vfxContainer;

        // If loops > 0, wait for animation to complete
        // For now, estimate based on typical VFX duration
        if (event.loops && event.loops > 0) {
          const estimatedDuration = (event.loops * 500) / (event.speed || 1);
          await new Promise((resolve) =>
            setTimeout(resolve, estimatedDuration),
          );
        }
      },

      onStopVFX: async (event) => {
        const vfxContainer = vfxSpritesRef.current[event.vfxId];
        if (vfxContainer) {
          if (event.fadeOutDuration && event.fadeOutDuration > 0) {
            await animateValue(
              1,
              0,
              event.fadeOutDuration,
              (t) => t,
              (alpha) => {
                vfxContainer.alpha = alpha;
              },
              abortControllerRef.current?.signal,
            );
          }
          world.removeChild(vfxContainer);
          vfxContainer.destroy();
          delete vfxSpritesRef.current[event.vfxId];
        }
      },

      onCameraPan: async (event, easing) => {
        const gridWidth = GRID_WIDTH * gridTileSize;
        const gridHeight = GRID_HEIGHT * gridTileSize;
        const baseScaleX = container.clientWidth / gridWidth;
        const baseScaleY = container.clientHeight / gridHeight;
        const baseScale = Math.min(baseScaleX, baseScaleY, 1) * 0.9;
        const currentZoom = world.scale.x / baseScale;

        const startX =
          (container.clientWidth / 2 - world.position.x) / world.scale.x;
        const startY =
          (container.clientHeight / 2 - world.position.y) / world.scale.y;

        await animatePosition(
          { x: startX, y: startY },
          event.target,
          event.duration,
          easing,
          (pos) => {
            world.position.set(
              container.clientWidth / 2 - pos.x * world.scale.x,
              container.clientHeight / 2 - pos.y * world.scale.y,
            );
          },
          abortControllerRef.current?.signal,
        );
      },

      onCameraZoom: async (event, easing) => {
        const gridWidth = GRID_WIDTH * gridTileSize;
        const gridHeight = GRID_HEIGHT * gridTileSize;
        const baseScaleX = container.clientWidth / gridWidth;
        const baseScaleY = container.clientHeight / gridHeight;
        const baseScale = Math.min(baseScaleX, baseScaleY, 1) * 0.9;

        const startZoom = world.scale.x / baseScale;
        const centerX =
          (container.clientWidth / 2 - world.position.x) / world.scale.x;
        const centerY =
          (container.clientHeight / 2 - world.position.y) / world.scale.y;

        await animateValue(
          startZoom,
          event.zoom,
          event.duration,
          easing,
          (zoom) => {
            const newScale = baseScale * zoom;
            world.scale.set(newScale);
            world.position.set(
              container.clientWidth / 2 - centerX * newScale,
              container.clientHeight / 2 - centerY * newScale,
            );
          },
          abortControllerRef.current?.signal,
        );
      },

      onCameraShake: async (event) => {
        await shakeScreen(
          world,
          event.intensity,
          event.duration,
          abortControllerRef.current?.signal,
        );
      },

      onShowDialog: async (event) => {
        onDialogStateChange({
          visible: true,
          speaker: event.speaker,
          portrait: event.portrait,
          text: "",
        });

        // Typewriter effect
        if (event.typeSpeed && event.typeSpeed > 0) {
          await typewriterEffect(
            event.text,
            event.typeSpeed,
            (displayedText) => {
              onDialogStateChange({
                visible: true,
                speaker: event.speaker,
                portrait: event.portrait,
                text: displayedText,
              });
            },
            abortControllerRef.current?.signal,
          );
        } else {
          onDialogStateChange({
            visible: true,
            speaker: event.speaker,
            portrait: event.portrait,
            text: event.text,
          });
        }

        // Wait for duration
        if (event.duration > 0) {
          await new Promise((resolve) => setTimeout(resolve, event.duration));
        }
      },

      onHideDialog: async (event) => {
        if (event.duration && event.duration > 0) {
          // Fade out handled by CSS in DialogBox component
          await new Promise((resolve) => setTimeout(resolve, event.duration));
        }
        onDialogStateChange({
          visible: false,
          speaker: "",
          portrait: undefined,
          text: "",
        });
      },

      onFade: async (event) => {
        const startOpacity = event.direction === "in" ? 1 : 0;
        const endOpacity = event.direction === "in" ? 0 : 1;

        onFadeStateChange({
          visible: true,
          opacity: startOpacity,
          color: event.color || "#000000",
        });

        await animateValue(
          startOpacity,
          endOpacity,
          event.duration,
          (t) => t,
          (opacity) => {
            onFadeStateChange({
              visible: true,
              opacity,
              color: event.color || "#000000",
            });
          },
          abortControllerRef.current?.signal,
        );

        if (event.direction === "in") {
          onFadeStateChange({
            visible: false,
            opacity: 0,
            color: event.color || "#000000",
          });
        }
      },

      onEventStart: (index, event) => {
        onEventIndexChange(index);
      },

      onEventComplete: (index, event) => {
        // Could add visual feedback here
      },

      onComplete: () => {
        onPlayStateChange(false);
        // Reset actors
        actorsRef.current = {};
        onActorsChange?.({});
      },

      onError: (error, event) => {
        console.error("[PreviewCanvas] Event error:", error, event);
      },
    };
  }, [
    onActorsChange,
    onDialogStateChange,
    onFadeStateChange,
    onEventIndexChange,
    onPlayStateChange,
  ]);

  // Handle play/stop
  useEffect(() => {
    if (!isInitialized || !worldRef.current) return;

    console.log(
      "[PreviewCanvas] isPlaying changed:",
      isPlaying,
      "isInitialized:",
      isInitialized,
    );

    if (isPlaying) {
      console.log(
        "[PreviewCanvas] Starting playback, events:",
        cutscene.events,
      );

      // Clear previous state
      actorsRef.current = {};
      Object.values(actorSpritesRef.current).forEach((sprite) => {
        worldRef.current?.removeChild(sprite);
        sprite.destroy();
      });
      actorSpritesRef.current = {};
      Object.values(vfxSpritesRef.current).forEach((sprite) => {
        worldRef.current?.removeChild(sprite);
        sprite.destroy();
      });
      vfxSpritesRef.current = {};
      onActorsChange?.({});

      // Reset camera
      const container = containerRef.current!;
      const world = worldRef.current!;
      const gridWidth = GRID_WIDTH * gridTileSize;
      const gridHeight = GRID_HEIGHT * gridTileSize;
      const baseScaleX = container.clientWidth / gridWidth;
      const baseScaleY = container.clientHeight / gridHeight;
      const baseScale = Math.min(baseScaleX, baseScaleY, 1) * 0.9;
      const finalScale = baseScale * cutscene.camera.initialZoom;

      world.scale.set(finalScale);
      world.position.set(
        container.clientWidth / 2 -
          cutscene.camera.initialPosition.x * finalScale,
        container.clientHeight / 2 -
          cutscene.camera.initialPosition.y * finalScale,
      );

      // Create and start engine
      abortControllerRef.current = new AbortController();
      const callbacks = createCallbacks();
      const engine = new CutsceneEngine(cutscene, callbacks);
      engineRef.current = engine;
      console.log("[PreviewCanvas] Engine created, calling play()");
      engine.play();
    } else {
      // Stop engine
      engineRef.current?.stop();
      engineRef.current = null;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    }
  }, [isPlaying, isInitialized, cutscene, createCallbacks, onActorsChange]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative bg-[#161e29] overflow-hidden"
    >
      {/* Fade overlay */}
      {fadeState.visible && (
        <div
          className="absolute inset-0 z-[100] pointer-events-none"
          style={{
            backgroundColor: fadeState.color,
            opacity: fadeState.opacity,
          }}
        />
      )}
    </div>
  );
};
